import {
  CustomFieldProjection,
  IDate,
  LeadSource,
  PetPolicyFieldKind,
  UUID,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

import {
  DistributionListings,
  HomeListingView,
} from '@new/domains/properties/screens/propertyDistributionChannels/DistributionChannels.types'

import {
  DistributionChannelDataField,
  DistributionChannelFieldValue,
} from './OtaDistributionChannel/OtaDistributionChannel.types'

interface GetUpdatedHomeDistributionInfoProps {
  homeDistributionInfo?: HomeListingView
  customFieldsInfo?: CustomFieldProjection[]
  listingDistribution?: DistributionListings
}

interface HomeInfoProps {
  petPolicy: string
  petCount: string
}

export interface DistributionDataProps
  extends
    Omit<HomeListingView, 'active' | 'apiConnected' | 'createdAt' | 'homeId' | 'source' | 'title'>,
    DistributionListings {
  active?: boolean
  apiConnected?: boolean
  createdAt?: IDate
  homeId?: UUID
  homeInfo?: HomeInfoProps
  source?: LeadSource
  title?: string
  home?: {
    name?: string
  }
}

interface GetDistributionInfoProps {
  distributionData?: DistributionDataProps
  distributionTitles: DistributionChannelDataField
}

interface DistributionInfoProps {
  distributionTitle: string
  distributionValue: string | string[]
}

export const getDistributionInfo = ({
  distributionData,
  distributionTitles,
}: GetDistributionInfoProps): DistributionInfoProps[] => {
  const mappedDistributionData = Object.keys(distributionTitles).map(valueKey => {
    const currentTitle = distributionTitles[valueKey].title

    if (distributionData == null) {
      return {
        distributionTitle: currentTitle,
        distributionValue: '-',
      }
    }

    const formatValue = distributionTitles[valueKey].format
    const keys = valueKey.split('.')

    if (keys.length === 1) {
      const distributionValue = distributionData[keys[0]]
      const formattedDistributionValue = formatValue
        ? formatValue(distributionValue)
        : distributionValue

      return {
        distributionTitle: currentTitle,
        distributionValue: formattedDistributionValue || '-',
      }
    }

    const distributionValue = keys.reduce(
      (accValue, currentValue) =>
        accValue ? accValue[currentValue] : distributionData[currentValue],
      '',
    )

    const validValue = distributionValue != null ? distributionValue : ''

    const formattedDistributionValue = formatValue ? formatValue(validValue) : distributionValue

    return {
      distributionTitle: currentTitle,
      distributionValue: formattedDistributionValue || '-',
    }
  })

  return mappedDistributionData
}

const PET_COUNT_ALLOWED_ID = 'a12dbecc-26f7-11ea-a680-503eaadea111'
const PET_POLICY_ID = 'b4cb8ec8-241e-11eb-b8aa-93b9b718e7ca'

const CHILDREN_ALLOWED_ID = '135e30e8-11a4-4db7-ae5a-607462f3d809'
const EVENTS_ALLOWED_ID = '4299e5a2-80d4-460b-b9be-c3d6e0b10117'
const SMOKING_ALLOWED_ID = '2ef87a24-5aa4-463a-8669-ca332ddca9fc'

export const getUpdatedHomeDistributionInfo = ({
  customFieldsInfo,
  homeDistributionInfo,
  listingDistribution,
}: GetUpdatedHomeDistributionInfoProps): DistributionDataProps => {
  const petPolicy = customFieldsInfo?.find(customField => customField.id === PET_POLICY_ID)?.value
  const petCount = customFieldsInfo?.find(
    customField => customField.id === PET_COUNT_ALLOWED_ID,
  )?.value

  const formattedPetPolicy = petPolicy?.toUpperCase().replace(/"/g, '')
  const petCountValue = formattedPetPolicy === PetPolicyFieldKind.approved ? petCount : '0'

  const childrenAllowed = customFieldsInfo?.find(
    customField => customField.id === CHILDREN_ALLOWED_ID,
  )?.value
  const eventsAllowed = customFieldsInfo?.find(
    customField => customField.id === EVENTS_ALLOWED_ID,
  )?.value
  const smokingAllowed = customFieldsInfo?.find(
    customField => customField.id === SMOKING_ALLOWED_ID,
  )?.value

  const updatedHomeDistributionInfo = {
    ...homeDistributionInfo,
    ...listingDistribution,
    homeInfo: {
      childrenAllowed,
      eventsAllowed,
      smokingAllowed,
      petPolicy: formattedPetPolicy ?? 'No',
      petCount: petCountValue ?? '0',
    },
  }

  return updatedHomeDistributionInfo
}

export const formatBooleanToText = (value: DistributionChannelFieldValue): string =>
  value ? 'Yes' : 'No'

export const formatBooleanTextToText = (value: DistributionChannelFieldValue): string =>
  value === 'true' ? 'Yes' : 'No'

export const formatPetPolicy = (value: DistributionChannelFieldValue): string => {
  return value === PetPolicyFieldKind.approved ? 'Yes' : 'No'
}

interface GetFieldCharacterLimitMessageProps {
  currentFieldLength?: number
  characterLimit: number
  fieldName: string
  isMaxLimit?: boolean
}

export const getFieldCharacterLimitMessage = ({
  currentFieldLength = 0,
  characterLimit,
  fieldName,
  isMaxLimit,
}: GetFieldCharacterLimitMessageProps): string => {
  const hasPassedLimitOnField = isMaxLimit
    ? currentFieldLength > characterLimit
    : currentFieldLength < characterLimit

  if (!hasPassedLimitOnField) {
    return ''
  }

  if (isMaxLimit) {
    const currentOverflow = Math.abs(characterLimit - currentFieldLength)

    return `${fieldName} should be less than ${characterLimit} characters. Remove at least ${currentOverflow} to meet the limit.`
  }

  const numberOfCharactersNeeded = Math.abs(currentFieldLength - characterLimit)

  return `${fieldName} must be more than ${characterLimit} characters. Add at least ${numberOfCharactersNeeded} characters to meet the limit.`
}
